import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Last updated: March 7, 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
          <section>
            <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              CryptoPulse Pro ("we", "our", or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your 
              information when you use our Bitcoin trading and prediction platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">
              When you create an account, we collect:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Email address</li>
              <li>Full name (optional)</li>
              <li>Password (encrypted)</li>
              <li>Account preferences</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.2 Trading Data</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Portfolio holdings and transactions</li>
              <li>AI prediction requests and history</li>
              <li>Price alerts and preferences</li>
              <li>Trading simulator activity</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.3 Payment Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              Payment processing is handled by Stripe. We do not store your credit card details. 
              Stripe collects and processes:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Credit/debit card information</li>
              <li>Billing address</li>
              <li>Payment history</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.4 Exchange API Keys (Optional)</h3>
            <p className="text-muted-foreground leading-relaxed">
              If you connect your Binance account:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>API keys are encrypted on your device</li>
              <li>We never store your API secret keys in plain text</li>
              <li>Keys are used only for authorized trading operations</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">2.5 Technical Data</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>IP address and device information</li>
              <li>Browser type and version</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Provide and maintain our services</li>
              <li>Process your transactions and subscriptions</li>
              <li>Generate AI-powered Bitcoin predictions</li>
              <li>Send service updates and notifications</li>
              <li>Improve our platform and user experience</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">4. Data Sharing and Disclosure</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We do not sell your personal information. We may share data with:
            </p>
            
            <h3 className="text-xl font-semibold mb-3 mt-6">4.1 Service Providers</h3>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Google (Gemini AI):</strong> AI predictions (anonymized data)</li>
              <li><strong>CoinGecko:</strong> Market data (no personal info shared)</li>
              <li><strong>MongoDB Atlas:</strong> Database hosting</li>
            </ul>

            <h3 className="text-xl font-semibold mb-3 mt-6">4.2 Legal Requirements</h3>
            <p className="text-muted-foreground leading-relaxed">
              We may disclose information if required by law, court order, or government request.
            </p>

            <h3 className="text-xl font-semibold mb-3 mt-6">4.3 Business Transfers</h3>
            <p className="text-muted-foreground leading-relaxed">
              If CryptoPulse Pro is acquired or merged, your data may be transferred to the new entity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">5. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We implement industry-standard security measures:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>AES-256 encryption for sensitive data</li>
              <li>HTTPS/TLS for all communications</li>
              <li>Bcrypt password hashing</li>
              <li>Regular security audits</li>
              <li>Secure database access controls</li>
              <li>API key encryption</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              However, no method of transmission over the internet is 100% secure. 
              We cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">6. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate information</li>
              <li><strong>Erasure:</strong> Request deletion of your account and data</li>
              <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Objection:</strong> Opt-out of certain data uses</li>
              <li><strong>Withdraw consent:</strong> Revoke permissions at any time</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise these rights, contact us at: <strong>support@cryptopulsepro.io</strong>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">7. Cookies and Tracking</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Keep you logged in</li>
              <li>Remember your preferences</li>
              <li>Analyze usage patterns</li>
              <li>Improve platform performance</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              You can control cookies through your browser settings. Disabling cookies may 
              limit platform functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">8. Third-Party Services</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              Our platform integrates with:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li><strong>Stripe:</strong> <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Privacy Policy</a></li>
              <li><strong>Google Gemini:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Privacy Policy</a></li>
              <li><strong>Binance:</strong> <a href="https://www.binance.com/en/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Binance Privacy Policy</a></li>
              <li><strong>CoinGecko:</strong> <a href="https://www.coingecko.com/en/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">CoinGecko Privacy Policy</a></li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              We are not responsible for third-party privacy practices. Please review their policies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">9. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your data:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>While your account is active</li>
              <li>As required by law (tax records: 7 years)</li>
              <li>To resolve disputes or enforce agreements</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              After account deletion, we may retain anonymized data for analytics.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">10. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed">
              CryptoPulse Pro is not intended for users under 18. We do not knowingly collect 
              data from children. If you believe a child has provided us with personal information, 
              contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">11. International Users</h2>
            <p className="text-muted-foreground leading-relaxed">
              Your data may be processed in the United States or other countries. By using 
              CryptoPulse Pro, you consent to international data transfer. We comply with 
              applicable data protection laws including GDPR and CCPA.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">12. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. Changes will be posted on 
              this page with an updated "Last updated" date. Continued use of our platform 
              after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4">13. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              For privacy-related questions or requests:
            </p>
            <div className="bg-muted p-6 rounded-lg">
              <p className="font-semibold mb-2">CryptoPulse Pro</p>
              <p className="text-muted-foreground">Email: support@cryptopulsepro.io</p>
              <p className="text-muted-foreground">Website: https://cryptopulsepro.io</p>
            </div>
          </section>

          <section className="border-t border-border pt-8 mt-8">
            <h2 className="text-2xl font-semibold mb-4">GDPR Rights (EU Users)</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              If you are in the European Economic Area, you have additional rights under GDPR:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Right to be informed about data processing</li>
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Rights related to automated decision-making</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              To exercise GDPR rights, contact: support@cryptopulsepro.io
            </p>
          </section>

          <section className="border-t border-border pt-8 mt-8">
            <h2 className="text-2xl font-semibold mb-4">CCPA Rights (California Users)</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              California residents have specific rights under CCPA:
            </p>
            <ul className="list-disc pl-6 text-muted-foreground space-y-2">
              <li>Right to know what personal information is collected</li>
              <li>Right to know if personal information is sold or disclosed</li>
              <li>Right to say no to the sale of personal information</li>
              <li>Right to access your personal information</li>
              <li>Right to deletion of personal information</li>
              <li>Right to non-discrimination for exercising rights</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              <strong>Note:</strong> We do NOT sell your personal information.
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-border">
          <Button onClick={() => navigate('/')} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;